import { useState } from 'react'
import { NavLink, Outlet } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { apiFetch } from '@/lib/api-client'
import {
  Banknote,
  BarChart3,
  BookOpen,
  Building2,
  Calendar,
  Camera,
  CheckSquare,
  ChevronDown,
  ChevronRight,
  ClipboardCheck,
  ClipboardList,
  Coins,
  Crown,
  Drum,
  FileText,
  Filter,
  Flag,
  HeartPulse,
  ListMusic,
  Inbox,
  LayoutDashboard,
  LogOut,
  MapPin,
  Megaphone,
  MessageSquare,
  Music,
  Newspaper,
  Plane,
  Radar,
  Route,
  Shield,
  ShieldAlert,
  ShoppingBag,
  Sliders,
  Sparkles,
  Ticket,
  Truck,
  UserCircle,
  Users,
  UsersRound,
  Link2,
  type LucideIcon,
} from 'lucide-react'
import { useAuthStore } from '@/lib/auth-store'
import { cn } from '@/lib/utils'
import { isPlatformAdminEmail, ROUTE_FEATURE_MAP } from '@ovation/core'
import { useFeatureChecker } from '@/lib/use-feature-enabled'
import { Logo } from './Logo'
import { HelixVaultLink } from './HelixVaultLink'
import { ArtistSwitcher } from './ArtistSwitcher'
import { OrganisationSwitcher } from './OrganisationSwitcher'
import { PerformanceSwitcher } from './PerformanceSwitcher'

// ── Nav definitions ─────────────────────────────────────────────────────────
//
// The sidebar is organised by Org → Artist → Performances scope, with the
// per-performance section grouped by the 9-stage workflow. Each section is
// collapsible; expansion state is persisted in the auth store so it survives
// reloads.
//
// Routes are unchanged from the previous flat nav — only their grouping is
// new. Each module's page still owns its own data fetching; future work will
// scope pages off `activePerformance` from the auth store.

interface NavItem {
  to: string
  label: string
  icon: LucideIcon
  end?: boolean
}

interface NavStage {
  id: string
  label: string
  items: readonly NavItem[]
}

const TOP_LEVEL_NAV: readonly NavItem[] = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/assistant', label: 'Assistant', icon: MessageSquare },
]

const ORG_NAV: readonly NavItem[] = [
  { to: '/roster', label: 'Roster', icon: Building2, end: true },
  { to: '/roster/pipeline', label: 'Roster pipeline', icon: Sparkles },
  { to: '/roster/users', label: 'Org users', icon: Users },
  { to: '/roster/billing', label: 'Billing', icon: BarChart3 },
  { to: '/org/settings', label: 'Org settings & modules', icon: Sliders },
]

const ARTIST_NAV: readonly NavItem[] = [
  { to: '/audience', label: 'Social & streaming', icon: Radar },
  { to: '/press', label: 'Press & Media', icon: Newspaper },
  { to: '/marketing', label: 'Marketing & Advertising', icon: Megaphone },
  { to: '/merch', label: 'Merch', icon: ShoppingBag },
  { to: '/analytics', label: 'Analytics', icon: BarChart3 },
  { to: '/royalties', label: 'Royalties', icon: Coins },
  { to: '/team', label: 'Artist Team', icon: UsersRound },
  { to: '/settings/artist', label: 'Artist settings & modules', icon: Sliders },
]

const PERFORMANCE_INDEX: readonly NavItem[] = [
  { to: '/performances', label: 'All performances', icon: Route, end: true },
]

const PERFORMANCE_STAGES: readonly NavStage[] = [
  {
    id: 'stage1',
    label: '1 · Marketing',
    items: [
      { to: '/marketing', label: 'Brokers & marketing', icon: Megaphone },
    ],
  },
  {
    id: 'stage2',
    label: '2 · Enquiry',
    items: [
      { to: '/inbox', label: 'Inbox', icon: Inbox },
      { to: '/pipeline', label: 'Pipeline', icon: Sparkles },
      { to: '/settings/epks', label: 'EPK content', icon: FileText },
    ],
  },
  {
    id: 'stage3',
    label: '3 · Booking',
    items: [
      { to: '/contracts', label: 'Contracts', icon: FileText },
    ],
  },
  {
    id: 'stage4',
    label: '4 · Planning & Logistics',
    items: [
      { to: '/travel', label: 'Travel', icon: Plane },
      { to: '/logistics', label: 'Logistics', icon: Truck },
      { to: '/venues', label: 'Venues', icon: Building2 },
      { to: '/territory', label: 'Territory', icon: MapPin },
      { to: '/rehearsals', label: 'Rehearsals', icon: Drum },
    ],
  },
  {
    id: 'stage5',
    label: '5 · Performance Prep',
    items: [
      { to: '/advancing', label: 'Advancing', icon: ClipboardCheck },
      { to: '/rider', label: 'Technical Rider', icon: ClipboardList },
      { to: '/setlists', label: 'Setlists', icon: Music },
      { to: '/vip', label: 'VIP', icon: Crown },
      { to: '/crew/pool', label: 'Talent pool', icon: UserCircle },
      { to: '/cancellations', label: 'Cancellations', icon: ShieldAlert },
    ],
  },
  {
    id: 'stage6',
    label: '6 · Performance',
    items: [
      { to: '/shows', label: 'Shows', icon: Calendar },
      { to: '/ticketing', label: 'Ticketing', icon: Ticket },
      { to: '/merch', label: 'Merch', icon: ShoppingBag },
      { to: '/wellness', label: 'Wellness Guardian', icon: HeartPulse },
    ],
  },
  {
    id: 'stage7',
    label: '7 · Review',
    items: [
      { to: '/debriefs', label: 'Debriefs', icon: BookOpen },
    ],
  },
  {
    id: 'stage8',
    label: '8 · Accounts',
    items: [
      { to: '/finance/documents', label: 'Finance docs', icon: FileText },
      { to: '/payments', label: 'Payments', icon: Banknote },
    ],
  },
  {
    id: 'stage9',
    label: '9 · Sign-off',
    items: [
      { to: '/tours', label: 'Tour close-out', icon: Flag },
    ],
  },
]

const CREW_NAV: readonly NavItem[] = [
  { to: '/crew/dashboard', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/crew/profile', label: 'My profile', icon: UserCircle },
  { to: '/crew/availability', label: 'Availability', icon: Calendar },
  { to: '/crew/engagements', label: 'Engagements', icon: Sparkles },
  { to: '/crew/checklist', label: 'Checklist', icon: CheckSquare },
  { to: '/crew/photos', label: 'Photos', icon: Camera },
  { to: '/crew/itineraries', label: 'Itineraries', icon: MapPin },
  { to: '/crew/messages', label: 'Messages', icon: MessageSquare },
]

const SETTINGS_NAV: readonly NavItem[] = [
  { to: '/integrations', label: 'Integrations', icon: Link2 },
  { to: '/email-rules', label: 'Routing', icon: Filter },
  { to: '/team-permissions', label: 'Team permissions', icon: Shield },
]

export function AppShell() {
  const user = useAuthStore((s) => s.user)
  const logout = useAuthStore((s) => s.logout)
  const navExpanded = useAuthStore((s) => s.navExpanded)
  const setNavExpanded = useAuthStore((s) => s.setNavExpanded)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  if (!user) return null

  const isFreeCrew = user.accountType === 'free' && !user.organisationId
  const hasOrg = !!user.organisationId
  const hasArtist = !!user.artistId
  const isAdmin =
    isPlatformAdminEmail(user.email) || user.accountType === 'internal'

  const isFeatureEnabled = useFeatureChecker()

  const isItemVisible = (item: NavItem): boolean => {
    if (item.to === '/marketing') {
      return (
        isFeatureEnabled('marketing') ||
        isFeatureEnabled('broker') ||
        isFeatureEnabled('ticketing')
      )
    }
    const feature = ROUTE_FEATURE_MAP[item.to]
    if (!feature) return true
    return isFeatureEnabled(feature)
  }

  const filteredOrgNav = ORG_NAV.filter(isItemVisible)
  const filteredArtistNav = ARTIST_NAV.filter(isItemVisible)
  const filteredTopLevelNav = TOP_LEVEL_NAV.filter(isItemVisible)
  const filteredPerformanceIndex = PERFORMANCE_INDEX.filter(isItemVisible)
  const filteredStages = PERFORMANCE_STAGES.map((s) => ({
    ...s,
    items: s.items.filter(isItemVisible),
  })).filter((s) => s.items.length > 0)
  const filteredSettingsNav = SETTINGS_NAV.filter(isItemVisible)

  const { data: crewPermissions } = useQuery<Record<string, Record<string, boolean>>>({
    queryKey: ['crew-permissions'],
    queryFn: () => apiFetch('/crew/profile/permissions'),
    enabled: isFreeCrew,
  })

  const mergedPerms: Record<string, boolean> = {}
  if (crewPermissions) {
    for (const artistPerms of Object.values(crewPermissions)) {
      for (const [key, val] of Object.entries(artistPerms)) {
        if (val) mergedPerms[key] = true
      }
    }
  }

  const CREW_GATED_NAV: NavItem[] = [
    ...(mergedPerms.view_shows ? [{ to: '/shows', label: 'Shows', icon: Music }] : []),
    ...(mergedPerms.view_setlists ? [{ to: '/setlists', label: 'Setlists', icon: ListMusic }] : []),
    ...(mergedPerms.submit_debriefs ? [{ to: '/debriefs', label: 'Debriefs', icon: ClipboardCheck }] : []),
    ...(mergedPerms.view_travel ? [{ to: '/travel', label: 'Travel', icon: Plane }] : []),
    ...(mergedPerms.view_logistics ? [{ to: '/logistics', label: 'Logistics', icon: Truck }] : []),
    ...(mergedPerms.view_merch ? [{ to: '/merch', label: 'Merch', icon: ShoppingBag }] : []),
  ]

  const onNavClick = () => setSidebarOpen(false)
  const isExpanded = (id: string) => !!navExpanded[id]
  const toggle = (id: string) => setNavExpanded(id, !navExpanded[id])

  return (
    <div className="flex h-dvh">
      <button
        type="button"
        onClick={() => setSidebarOpen(true)}
        className="fixed top-4 left-4 z-50 rounded-md bg-ovation-paper p-2 shadow-md border border-ovation-ink/10 lg:hidden"
        aria-label="Open menu"
      >
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>

      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex w-72 flex-col border-r border-ovation-ink/10 bg-ovation-paper px-4 py-6 transition-transform duration-200 lg:static lg:translate-x-0',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div className="mb-4 flex items-center justify-between px-2">
          <Logo />
          <button
            type="button"
            onClick={() => setSidebarOpen(false)}
            className="rounded-md p-1 hover:bg-ovation-ink/5 lg:hidden"
            aria-label="Close menu"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Context strip — Org / Artist / Performance switchers */}
        <div className="mb-3 flex flex-col gap-2 px-2">
          {hasOrg && <OrganisationSwitcher />}
          {hasArtist && user.artistMemberships.length > 0 && <ArtistSwitcher />}
          {hasArtist && <PerformanceSwitcher />}
          {user.helixVaultUrl ? (
            <HelixVaultLink url={user.helixVaultUrl} compact />
          ) : null}
        </div>

        <nav className="flex flex-1 flex-col gap-1 overflow-y-auto">
          {/* Always-on top-level items */}
          {filteredTopLevelNav.length > 0 && (
            <FlatNav items={filteredTopLevelNav} onNavClick={onNavClick} />
          )}

          {/* Organisation section */}
          {hasOrg && filteredOrgNav.length > 0 && (
            <CollapsibleSection
              id="org"
              label="Organisation"
              expanded={isExpanded('org')}
              onToggle={() => toggle('org')}
            >
              <FlatNav items={filteredOrgNav} onNavClick={onNavClick} />
            </CollapsibleSection>
          )}

          {/* Artist section — cross-performance, brand-level items */}
          {hasArtist && filteredArtistNav.length > 0 && (
            <CollapsibleSection
              id="artist"
              label="Artist"
              expanded={isExpanded('artist')}
              onToggle={() => toggle('artist')}
            >
              <FlatNav items={filteredArtistNav} onNavClick={onNavClick} />
            </CollapsibleSection>
          )}

          {/* Performances section — 9-stage flow */}
          {hasArtist && filteredStages.length > 0 && (
            <CollapsibleSection
              id="performances"
              label="Performances"
              expanded={isExpanded('performances')}
              onToggle={() => toggle('performances')}
            >
              {filteredPerformanceIndex.length > 0 && (
                <FlatNav items={filteredPerformanceIndex} onNavClick={onNavClick} />
              )}
              <div className="my-2 border-t border-ovation-ink/5" />
              {filteredStages.map((stage) => (
                <CollapsibleSection
                  key={stage.id}
                  id={`perf-${stage.id}`}
                  label={stage.label}
                  variant="nested"
                  expanded={isExpanded(`perf-${stage.id}`)}
                  onToggle={() => toggle(`perf-${stage.id}`)}
                >
                  <FlatNav items={stage.items} onNavClick={onNavClick} />
                </CollapsibleSection>
              ))}
            </CollapsibleSection>
          )}

          {/* Crew section — for free crew accounts */}
          {isFreeCrew && (
            <CollapsibleSection
              id="crew"
              label="Crew"
              expanded={isExpanded('crew')}
              onToggle={() => toggle('crew')}
            >
              <FlatNav items={CREW_NAV} onNavClick={onNavClick} />
            </CollapsibleSection>
          )}
          {isFreeCrew && CREW_GATED_NAV.length > 0 && (
            <CollapsibleSection
              id="crew-access"
              label="Artist Access"
              expanded={isExpanded('crew-access')}
              onToggle={() => toggle('crew-access')}
            >
              <FlatNav items={CREW_GATED_NAV} onNavClick={onNavClick} />
            </CollapsibleSection>
          )}
          {!isFreeCrew && (
            <CollapsibleSection
              id="my-crew"
              label="My crew profile"
              expanded={isExpanded('my-crew')}
              onToggle={() => toggle('my-crew')}
            >
              <FlatNav
                items={[
                  { to: '/crew/profile', label: 'My profile', icon: UserCircle, end: true },
                ]}
                onNavClick={onNavClick}
              />
            </CollapsibleSection>
          )}

          {/* Settings + integrations — bottom of the main nav, always available */}
          {filteredSettingsNav.length > 0 && (
            <CollapsibleSection
              id="settings"
              label="Settings"
              expanded={isExpanded('settings')}
              onToggle={() => toggle('settings')}
            >
              <FlatNav items={filteredSettingsNav} onNavClick={onNavClick} />
            </CollapsibleSection>
          )}

          {isAdmin && (
            <CollapsibleSection
              id="platform"
              label="Platform"
              expanded={isExpanded('platform')}
              onToggle={() => toggle('platform')}
            >
              <FlatNav
                items={[{ to: '/admin', label: 'Admin Panel', icon: Shield, end: true }]}
                onNavClick={onNavClick}
              />
            </CollapsibleSection>
          )}
        </nav>

        <div className="mt-4 border-t border-ovation-ink/10 pt-4">
          <div className="mb-2 px-3 text-xs text-ovation-muted">
            <div className="truncate text-ovation-ink">{user.displayName}</div>
            <div className="truncate text-[10px]">{user.email}</div>
            <div className="mt-1 capitalize">
              {user.orgRole ?? user.role ?? user.accountType}
            </div>
          </div>
          <button
            type="button"
            onClick={logout}
            className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm text-ovation-ink hover:bg-ovation-ink/5"
          >
            <LogOut className="h-4 w-4" />
            Sign out
          </button>
          <div className="mt-3 text-center text-[9px] uppercase tracking-[0.18em] text-ovation-muted/70">
            <a
              href="https://geekybee.net"
              target="_blank"
              rel="noreferrer"
              className="hover:text-ovation-ink"
            >
              A Geekybee.net product
            </a>
          </div>
        </div>
      </aside>
      <main className="flex-1 overflow-y-auto overflow-x-hidden p-3 pt-14 sm:p-4 sm:pt-14 lg:p-8 lg:pt-8">
        <Outlet />
      </main>
    </div>
  )
}

// ── Internal sub-components ────────────────────────────────────────────────

/** Flat list of NavLinks — used inside every collapsible section. */
function FlatNav({
  items,
  onNavClick,
}: {
  items: readonly NavItem[]
  onNavClick?: () => void
}) {
  return (
    <div className="flex flex-col gap-0.5">
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end ?? false}
          onClick={onNavClick}
          className={({ isActive }) =>
            cn(
              'flex items-center gap-3 rounded-md px-3 py-1.5 text-sm transition-colors',
              isActive
                ? 'bg-ovation-ink text-ovation-paper'
                : 'text-ovation-ink hover:bg-ovation-ink/5',
            )
          }
        >
          <item.icon className="h-4 w-4 flex-shrink-0" />
          <span className="truncate">{item.label}</span>
        </NavLink>
      ))}
    </div>
  )
}

/** Collapsible section header with persisted expand/collapse state. */
function CollapsibleSection({
  id,
  label,
  expanded,
  onToggle,
  variant = 'top',
  children,
}: {
  id: string
  label: string
  expanded: boolean
  onToggle: () => void
  variant?: 'top' | 'nested'
  children: React.ReactNode
}) {
  return (
    <div className={cn('flex flex-col', variant === 'top' ? 'mt-2 first:mt-0' : 'ml-1')}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        aria-controls={`section-${id}`}
        className={cn(
          'flex w-full items-center gap-1 rounded-md px-3 py-1 text-left transition-colors hover:bg-ovation-ink/5',
          variant === 'top'
            ? 'text-[10px] font-semibold uppercase tracking-[0.18em] text-ovation-muted'
            : 'text-[11px] font-medium text-ovation-ink/70',
        )}
      >
        {expanded ? (
          <ChevronDown className="h-3 w-3 flex-shrink-0" />
        ) : (
          <ChevronRight className="h-3 w-3 flex-shrink-0" />
        )}
        <span className="truncate">{label}</span>
      </button>
      {expanded && (
        <div id={`section-${id}`} className={cn('mt-1', variant === 'nested' && 'ml-3')}>
          {children}
        </div>
      )}
    </div>
  )
}
